
import * as React from 'react';
import { useState } from "react";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Stepper, Step, StepLabel, Divider, Typography, Modal, Button, Fade, CircularProgress } from '@mui/material';
import * as Bluetooth from 'react-bluetooth';
import BluetoothConnected from '@mui/icons-material/BluetoothConnected'
import BluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled';
import DeviceConnection from './DeviceConnection';
import WifiSetup from './WifiSetup';
import MqttSetup from './MqttSetup';
import GithubBearerSetup from './GithubBearerSetup';
import CompletionPage from './CompletionPage';
import SrinLogo from './images/SrinDev.png';

const steps = ['기기 연결', 'Wi-Fi 설정', 'MQTT 설정', 'GitHub Token 설정'];

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1D1D1D',
    },
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    text: {
      primary: '#ffffff',
      secondary: '#aaaaaa',
    },
  },
});

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  textAlign: 'center',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 2,
};

function App() {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  
  const [activeStep, setActiveStep] = useState(0);
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [deviceServer, setDeviceServer] = useState()

  const [wifiList, setWifiList] = useState([])
  const [wifiSsid, setWifiSsid] = useState('')
  const [wifiPassword, setWifiPassword] = useState('')

  const [mqttBrokerIp, setMqttBrokerIp] = useState('')
  const [mqttUsername, setMqttUsername] = useState('')
  const [mqttPassword, setMqttPassword] = useState('')
  const [mqttClientName, setMqttClientName] = useState('')
  const [mqttPortNumber, setMqttPortNumber] = useState(1883)
  const [mqttTopic, setMqttTopic] = useState('esp32_matrix')

  const [githubBearer, setGithubBearer] = useState('')
  
  const [characteristicTx, setCharacteristicTx] = useState()

  const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);
  const handleRestart = () => setActiveStep(0); // Restart the process.

  const handleSetIsConnected = (value) => {
    setIsConnecting(() => false)
    setIsConnected((prevIsConnected) => {
      if (prevIsConnected && !value) {
        handleOpen()
      }
      if (!value) {
        setDeviceServer()
      }
      return value
    })
  }

  const enc = new TextEncoder()
  const dec = new TextDecoder('utf-8')
    
  const jsonTypeEnum = {
    WIFI_LIST: 0,
    REMOVE_SETTINGS: 1,
    SAVE_SETTINGS: 2,
  }

  const mainContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    flexDirection: 'column'
  };

  const stepContentStyle = {
    maxWidth: '600px',
    width: '100%',
    padding: '20px',
    backgroundColor: '#282c34',
    borderRadius: '8px'
  };

  async function handleDeviceDisconnect() {
    deviceServer.disconnect()
  }

  async function sendSettings() {
    const settingsJson = {
      t: jsonTypeEnum.SAVE_SETTINGS,
      d: [wifiSsid, wifiPassword, mqttBrokerIp, mqttUsername, mqttPassword, mqttClientName, Number(mqttPortNumber), mqttTopic, githubBearer]
    }
    await characteristicTx.writeValue(enc.encode(JSON.stringify(settingsJson)))
    handleSetIsConnected(false)
  }

  async function removeESP32Settings() {
    const settingsJson = {
      t: jsonTypeEnum.REMOVE_SETTINGS
    }
    await characteristicTx.writeValue(enc.encode(JSON.stringify(settingsJson)))
    handleSetIsConnected(false)
  }

  async function GetDeviceAsync() {
  
    handleSetIsConnected(false)
    const isAvailable = await Bluetooth.getAvailabilityAsync();
    if (!isAvailable) {
      return;
    }
  
    const options = {
      optionalServices: ['e85467ac-6c22-43c3-89ed-0f1b0ceaa584'],
      // iOS(CoreBluetooth)는 기기의 GAP 이름을 캐시해 두고 peripheral.name 으로 준다.
      // 그래서 펌웨어에서 이름을 바꿔도 폰에는 옛 이름이 남아 namePrefix 에 걸리지 않는다.
      // 서비스 UUID 로도 찾을 수 있게 두 조건을 OR 로 준다(filters 배열은 OR 이다).
      filters: [
        { namePrefix: 'SrinDev LED Matrix' },
        { services: ['e85467ac-6c22-43c3-89ed-0f1b0ceaa584'] }
      ],
    };
    
    try {
      setIsConnecting(() => true)
      const result = await Bluetooth.requestDeviceAsync(options);
      if (result.type === 'cancel') {
        return;
      }
  
      const { device } = result;
      // console.log('Success: Got any device: ', device);
      if (device.gatt) {
        const server = await device.gatt.connect();
        const service = await server.getPrimaryService('e85467ac-6c22-43c3-89ed-0f1b0ceaa584');
        const characteristicRx = await service.getCharacteristic('a3d700e6-2914-414e-b968-4a0d21e017da');
        // console.log(`Bluetooth: Got Rx characteristic:`, characteristicRx);
        characteristicRx.startNotifications().then(_ => {
          characteristicRx.addEventListener('characteristicvaluechanged', (e) => {
            var arrBuffer = new Uint8Array(e.target.value.buffer)
            // console.log(dec.decode(arrBuffer))
            const recieve = dec.decode(arrBuffer);
            // console.log(recieve)
            // setValue((prev) => (prev + "\n" + recieve))
            const receiveJson = JSON.parse(recieve)

            if (receiveJson != null && receiveJson.hasOwnProperty('t') && receiveJson['t'] == jsonTypeEnum.WIFI_LIST) {
              setWifiList(() => receiveJson.hasOwnProperty('d') ? receiveJson['d'] : [])
              // console.log(receiveJson['d'])
            }
            // console.log(e.target.value.buffer)
          })
        })
        
        const newCharacteristicTx = await service.getCharacteristic('86ee9d75-1d4d-4f2d-b9de-d4b5951ef526');
        setCharacteristicTx(() => newCharacteristicTx)
        // console.log(`Bluetooth: Got Tx characteristic:`, characteristicTx);
        
        // characteristicTx.writeValue(enc.encode("1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 1234567 "))
        handleSetIsConnected(true)
        setDeviceServer(() => server)

        device.addEventListener("gattserverdisconnected", (e) => {
          handleSetIsConnected(false)
        })
        
      } else {
        // TODO: Bacon: Can we connect to the GATT or is that a no-op?
        console.error(`Error: connected device did not have a GATT`)
        handleSetIsConnected(false)
      }
    } catch (error) {
      console.log(`Error: Couldn't get any device`, error);
      console.error(`Error: Couldn't get any device`, error);
      handleSetIsConnected(false)
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Box style={mainContainerStyle}>
        <CssBaseline />
        {activeStep === steps.length ? (
          <Fade in={activeStep === steps.length} timeout={1000}>
            <div>
              <CompletionPage handleRestart={handleRestart} />
            </div>
          </Fade>
        ) : (
          <Box style={stepContentStyle}>
            <Box style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BluetoothConnected style={{ fontSize: 40, color: '#4dabf5' }} />
              <Typography variant="h5" style={{ marginLeft: '8px' }}>
                LED Matrix 설정
              </Typography>
            </Box>
            <Divider style={{ margin: '12px 0 12px 0' }} />
            <Stepper activeStep={activeStep} style={{ margin: '8px' }} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Divider style={{ margin: '12px 0 12px 0' }} />
            <div>
              <Fade in={activeStep === 0} timeout={1000}>
                <div>
                  {activeStep === 0 && 
                    <DeviceConnection
                      handleNext={handleNext}
                      GetDeviceAsync={GetDeviceAsync}
                      isConnected={isConnected}
                      handleRemoveESP32Settings={removeESP32Settings}
                      isConnecting={isConnecting}
                      handleDeviceDisconnect={handleDeviceDisconnect}
                    />
                  }
                </div>
              </Fade>
              <Fade in={activeStep === 1} timeout={1000}>
                <div>
                  {activeStep === 1 &&
                    <WifiSetup
                      handleNext={handleNext}
                      handleBack={handleBack}
                      wifiList={wifiList}
                      wifiSsid={wifiSsid}
                      setWifiSsid={setWifiSsid}
                      wifiPassword={wifiPassword}
                      setWifiPassword={setWifiPassword}
                      isConnected={isConnected}
                      handleRestart={handleRestart}
                    />
                  }
                </div>
              </Fade>
              <Fade in={activeStep === 2} timeout={1000}>
                <div>
                  {activeStep === 2 &&
                    <MqttSetup
                      handleNext={handleNext}
                      handleBack={handleBack}
                      mqttBrokerIp={mqttBrokerIp}
                      setMqttBrokerIp={setMqttBrokerIp}
                      mqttUsername={mqttUsername}
                      setMqttUsername={setMqttUsername}
                      mqttPassword={mqttPassword}
                      setMqttPassword={setMqttPassword}
                      mqttClientName={mqttClientName}
                      setMqttClientName={setMqttClientName}
                      mqttPortNumber={mqttPortNumber}
                      setMqttPortNumber={setMqttPortNumber}
                      mqttTopic={mqttTopic}
                      setMqttTopic={setMqttTopic}
                      isConnected={isConnected}
                      handleRestart={handleRestart}
                    />
                  }
                </div>
              </Fade>
              <Fade in={activeStep === 3} timeout={1000}>
                <div>
                  {activeStep === 3 &&
                    <GithubBearerSetup
                      handleBack={handleBack}
                      handleNext={handleNext}
                      handleSendSettings={sendSettings}
                      githubBearer={githubBearer}
                      setGithubBearer={setGithubBearer}
                      isConnected={isConnected}
                      handleRestart={handleRestart}
                    />
                  }
                </div>
              </Fade>
            </div>
          </Box>
        )}
        <br />
        <img src={SrinLogo} style={{ opacity: 0.5 }} width={96} height={45} />
      </Box>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <BluetoothDisabledIcon style={{ fontSize: 100, color: '#ffef62' }} />
          <Typography variant="h6" style={{ marginTop: '8px', marginBottom: '8px' }}>
            블루투스 연결이 해제되었습니다.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            style={{ float: 'right' }}
          >
            확인
          </Button>
        </Box>
      </Modal>
    </ThemeProvider>
  );
}

export default App;
